import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TurnstileWidget from "../common/TurnstileWidget";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackContactSubmitted } from "../../lib/analytics";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";
const TURNSTILE_SITE_KEY = (
  import.meta.env.VITE_TURNSTILE_SITE_KEY || ""
).trim();

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
};

const ContactForm = () => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [statusMessage, setStatusMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleTurnstileToken = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  useEffect(() => {
    if (status !== "success" && status !== "error") return;
    const timeoutId = setTimeout(() => {
      setStatus("idle");
      setStatusMessage("");
    }, 6000);
    return () => clearTimeout(timeoutId);
  }, [status]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setStatus("sending");
      setStatusMessage("");

      const genericError = t("contact.form.genericError");

      try {
        const payload = {
          ...form,
          ...(TURNSTILE_SITE_KEY && turnstileToken
            ? { turnstileToken }
            : {}),
        };
        const res = await fetch(`${API_URL}/api/contact`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        await res.json();

        if (!res.ok) {
          throw new Error(genericError);
        }

        setStatus("success");
        setStatusMessage(t("contact.form.successMessage"));
        setForm(INITIAL_FORM);
        trackContactSubmitted(analyticsContext);
      } catch {
        setStatus("error");
        setStatusMessage(genericError);
      }
    },
    [form, t, turnstileToken, analyticsContext],
  );

  return (
    <section className="section topless">
      <div className="container">
        <div className="form-block" style={{ width: "100%" }}>
          <form
            className="contact-form"
            onSubmit={handleSubmit}
            data-wf-ignore="true"
          >
            <div className="contact-form-row">
              <div className="contact-form-field">
                <label htmlFor="firstName">
                  {t("contact.form.labels.firstName")}
                </label>
                <input
                  className="text-field w-input"
                  type="text"
                  id="firstName"
                  name="firstName"
                  placeholder={t("contact.form.placeholders.firstName")}
                  maxLength={100}
                  required
                  value={form.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="lastName">
                  {t("contact.form.labels.lastName")}
                </label>
                <input
                  className="text-field w-input"
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder={t("contact.form.placeholders.lastName")}
                  maxLength={100}
                  required
                  value={form.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div className="contact-form-field">
              <label htmlFor="email">{t("contact.form.labels.email")}</label>
              <input
                className="text-field w-input"
                type="email"
                id="email"
                name="email"
                placeholder={t("contact.form.placeholders.email")}
                maxLength={254}
                required
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
            <div className="contact-form-field">
              <label htmlFor="subject">
                {t("contact.form.labels.subject")}
              </label>
              <input
                className="text-field w-input"
                type="text"
                id="subject"
                name="subject"
                placeholder={t("contact.form.placeholders.subject")}
                maxLength={200}
                required
                value={form.subject}
                onChange={handleChange}
              />
            </div>
            <div className="contact-form-field">
              <label htmlFor="message">
                {t("contact.form.labels.message")}
              </label>
              <textarea
                className="text-field w-input"
                id="message"
                name="message"
                placeholder={t("contact.form.placeholders.message")}
                maxLength={5000}
                required
                rows={6}
                value={form.message}
                onChange={handleChange}
              />
            </div>
            {TURNSTILE_SITE_KEY && (
              <div
                className="contact-form-field"
                style={{ display: "flex", justifyContent: "center" }}
              >
                <TurnstileWidget
                  siteKey={TURNSTILE_SITE_KEY}
                  onToken={handleTurnstileToken}
                />
              </div>
            )}
            <button
              type="submit"
              className="button w-inline-block contact-submit-btn"
              disabled={
                status === "sending" ||
                (!!TURNSTILE_SITE_KEY && !turnstileToken)
              }
            >
              <div className="button-icon">
                <img
                  src="/assets/photos/Button-Icon.svg"
                  alt={t("contact.form.submitIconAlt")}
                  loading="lazy"
                  className="b-icon"
                />
              </div>
              <div className="button-txt">
                {status === "sending"
                  ? t("contact.form.submitting")
                  : t("contact.form.submit")}
              </div>
            </button>
          </form>

          {status === "success" && (
            <div className="contact-form-status success">
              <div>{statusMessage}</div>
            </div>
          )}

          {status === "error" && (
            <div className="contact-form-status error">
              <div>{statusMessage}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
